import { isOneClick, CAST_PILOT_ASSISTANT } from '@cast/constants';
import { BASE_PATHS, PRODUCT_BASE_PATHS } from '@cast/console-constants';
import type { ReportingMonetizationReadinessResponse } from '@cast/types';

import { makeOrgCostMonitoringAllocationGroupsLink } from 'features/organization/cost-report/links';

import { OnboardingTaskId } from './taskIds';

type ReadinessFlag = keyof Pick<
  ReportingMonetizationReadinessResponse,
  | 'hasConnectedCluster'
  | 'hasAllocationGroups'
  | 'hasBudgets'
  | 'hasAskedOpsPilotQuestion'
>;

export type OnboardingTaskDefinition = {
  id: OnboardingTaskId;
  label: string;
  recommended: boolean;
  getHref: () => string;
  /** Org-level readiness flag from GET …/reporting-monetization/readiness. */
  readinessFlag: ReadinessFlag;
};

const COST_REPORT_BASE_PATH = `${PRODUCT_BASE_PATHS.automation}/cost-report`;
const BUDGETS_PATH = `${PRODUCT_BASE_PATHS.automation}/budgets`;
const CLASSIC_CLUSTER_LIST_PATH = `${PRODUCT_BASE_PATHS.automation}/cluster-list`;

/** Connect-cluster CTA for Getting started — console-aware. */
export const getConnectClusterHref = (): string =>
  isOneClick() ? BASE_PATHS.clusters : CLASSIC_CLUSTER_LIST_PATH;

/**
 * Cast Radar / eBDR activation milestones.
 * Checked state comes from the org readiness API; Ask OpsPilot also ORs
 * same-browser chat history to bridge PostHog lag.
 *
 * Hrefs must resolve in both classic and OneClick (shared wizard in +split-me).
 */
export const ONBOARDING_TASK_DEFINITIONS: readonly OnboardingTaskDefinition[] =
  [
    {
      id: OnboardingTaskId.ConnectCluster,
      label: 'Connect a cluster',
      recommended: true,
      getHref: getConnectClusterHref,
      readinessFlag: 'hasConnectedCluster',
    },
    {
      id: OnboardingTaskId.CreateAllocationGroup,
      label: 'Create an allocation group',
      recommended: true,
      getHref: () => makeOrgCostMonitoringAllocationGroupsLink(),
      readinessFlag: 'hasAllocationGroups',
    },
    {
      id: OnboardingTaskId.CreateBudget,
      label: 'Create a budget',
      recommended: true,
      getHref: () => BUDGETS_PATH,
      readinessFlag: 'hasBudgets',
    },
    {
      id: OnboardingTaskId.AskOpsPilot,
      label: `Ask ${CAST_PILOT_ASSISTANT} a question`,
      recommended: true,
      getHref: () => COST_REPORT_BASE_PATH,
      readinessFlag: 'hasAskedOpsPilotQuestion',
    },
  ];
